/**
 * Fund and Cohort Payouts — each grant, then who it paid.
 *
 * GROUPED BY GRANT, NOT FLAT. A funder holds the question as "what did THIS
 * grant do", so the deposit is the top level and its payments sit under it. A
 * flat list makes somebody reconcile two funds by eye.
 *
 * A FUNDER IS NOT A SPONSOR WITH MEMBERS. A foundation funding a programme has
 * none: the mothers belong to the organisations that enrolled them and the
 * doulas to the ones that employ them. Every row here comes from a PAYMENT
 * record rather than a membership list, and there is deliberately no "our
 * members" count anywhere on the page.
 *
 * EVERY LINE CARRIES ITS MECHANICS. A funder asking about a payment reliably
 * asks four things — when, to whom, for what, and how the money moved. The last
 * was the one the previous version could not answer at all.
 *
 * "Not recorded" is shown as itself and never as a guess. A payout written
 * before the provider side had a method column has none on record, and that is
 * different from having been paid by some assumed default.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Building2, CircleDollarSign, CreditCard, Landmark, User } from 'lucide-react';

interface FundedPayment {
  payout_id: number;
  paid_to_kind: string;
  paid_to: string;
  served_by_org: string | null;
  for_member: string | null;
  amount: number;
  status: string;
  what_for: string | null;
  scheduled_on: string | null;
  paid_on: string | null;
  days_to_pay: number | null;
  method: string | null;
  card_last4: string | null;
  card_network: string | null;
  bank_last4: string | null;
  transaction_reference: string | null;
}

interface FundedGrant {
  fund_id: number;
  deposit_number: string | null;
  programme: string | null;
  purpose: string | null;
  deposited: number;
  committed: number;
  disbursed: number;
  remaining: number;
  deposit_date: string | null;
  members: FundedPayment[];
  providers: FundedPayment[];
  organisations_reached: number;
}

interface FundedView {
  sponsor_id: number;
  sponsor_name: string;
  deposited: number;
  committed: number;
  disbursed: number;
  remaining: number;
  grants: FundedGrant[];
  organisations_reached: number;
  message: string;
}

function money(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function when(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export default function SponsorFundedPayments() {
  const { t } = useTranslation();
  const [data, setData] = useState<FundedView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await authFetchJson<FundedView>('/api/sponsor/v1/funded'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('sponsorFunded.loadFailed', 'We could not load this fund.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** How the money moved, said plainly, with "not recorded" as a real answer. */
  const Mechanism = ({ p }: { p: FundedPayment }) => {
    if (!p.method) {
      return (
        <span className="text-muted-foreground">
          {t('sponsorFunded.methodUnknown', 'Not recorded')}
        </span>
      );
    }
    const isCard = p.method === 'PREPAID_CARD' || Boolean(p.card_last4);
    const label = t(`sponsorFunded.method.${p.method}`, p.method.replace(/_/g, ' ').toLowerCase());
    return (
      <span className="flex items-start gap-1.5">
        {isCard ? (
          <CreditCard className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span>
          <span className="capitalize">{label}</span>
          {p.card_last4 && (
            <span className="block text-xs text-muted-foreground">
              {p.card_network ? `${p.card_network} ` : ''}•••• {p.card_last4}
            </span>
          )}
          {!p.card_last4 && p.bank_last4 && (
            <span className="block text-xs text-muted-foreground">•••• {p.bank_last4}</span>
          )}
          {p.transaction_reference && (
            <span className="block text-xs text-muted-foreground">{p.transaction_reference}</span>
          )}
        </span>
      </span>
    );
  };

  /** Scheduled and paid as separate facts; the gap is what a funder looks for. */
  const Timing = ({ p }: { p: FundedPayment }) => (
    <span className="block">
      <span className="block">{p.paid_on ? when(p.paid_on) : when(p.scheduled_on)}</span>
      <span className="block text-xs text-muted-foreground">
        {p.paid_on
          ? p.days_to_pay !== null
            ? t('sponsorFunded.daysAfter', 'paid {{count}} days after scheduled', { count: p.days_to_pay })
            : t('sponsorFunded.paidLabel', 'paid')
          : t('sponsorFunded.scheduledLabel', 'scheduled, not yet paid')}
      </span>
    </span>
  );

  const PaymentTable = ({ rows, kind }: { rows: FundedPayment[]; kind: 'member' | 'provider' }) => (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-left">
          <tr>
            <th className="p-3 font-medium">{t('sponsorFunded.paidTo', 'Paid to')}</th>
            <th className="p-3 font-medium">{t('sponsorFunded.forWhat', 'For')}</th>
            <th className="p-3 text-right font-medium">{t('sponsorFunded.amount', 'Amount')}</th>
            <th className="p-3 font-medium">{t('sponsorFunded.timing', 'Timing')}</th>
            <th className="p-3 font-medium">{t('sponsorFunded.howPaid', 'How paid')}</th>
            <th className="p-3 font-medium">{t('sponsorFunded.status', 'Status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={`${p.paid_to_kind}-${p.payout_id}`} className="border-b last:border-0 align-top">
              <td className="p-3">
                <span className="flex items-center gap-2">
                  {/* The icon repeats what the section heading says, so the
                      member/provider distinction is never colour-alone. */}
                  {kind === 'provider' ? (
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-medium">{p.paid_to}</span>
                </span>
                {p.served_by_org && (
                  <span className="mt-0.5 block text-xs text-muted-foreground">{p.served_by_org}</span>
                )}
              </td>
              <td className="p-3 text-muted-foreground">
                {p.what_for || '—'}
                {p.for_member && (
                  <span className="mt-0.5 block text-xs">
                    {t('sponsorFunded.careFor', 'care for {{name}}', { name: p.for_member })}
                  </span>
                )}
              </td>
              <td className="p-3 text-right tabular-nums">{money(p.amount)}</td>
              <td className="p-3 text-muted-foreground"><Timing p={p} /></td>
              <td className="p-3 text-muted-foreground"><Mechanism p={p} /></td>
              <td className="p-3">
                <Badge variant={p.status === 'paid' ? 'success' : 'secondary'}>
                  {p.status === 'paid'
                    ? t('sponsorFunded.paid', 'Paid')
                    : t('sponsorFunded.pending', 'Pending')}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <PageState variant="loading" />;

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      </PageContainer>
    );
  }

  const tiles = [
    { key: 'deposited', label: t('sponsorFunded.deposited', 'Granted'), value: data?.deposited },
    { key: 'committed', label: t('sponsorFunded.committed', 'Committed'), value: data?.committed },
    { key: 'disbursed', label: t('sponsorFunded.disbursed', 'Paid out'), value: data?.disbursed },
    { key: 'remaining', label: t('sponsorFunded.remaining', 'Remaining'), value: data?.remaining },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t('sponsorFunded.title', 'Fund and cohort payouts')}
        description={t(
          'sponsorFunded.description',
          'Each grant, and every payment made from it — to mothers and to the people and organisations caring for them.',
        )}
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((s) => (
          <div key={s.key} className="rounded-md border p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums">{money(s.value)}</p>
          </div>
        ))}
      </div>

      {!data || data.grants.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <CircleDollarSign className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {t('sponsorFunded.noGrants', 'This organisation holds no active fund.')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{data?.message}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {data.grants.map((g) => (
            <section key={g.fund_id}>
              <div className="rounded-t-md border border-b-0 bg-muted/30 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-base font-semibold">
                    {g.programme || t('sponsorFunded.untitledGrant', 'Grant')}
                  </h2>
                  {g.deposit_number && (
                    <span className="font-mono text-xs text-muted-foreground">{g.deposit_number}</span>
                  )}
                </div>
                {g.purpose && <p className="mt-1 text-sm text-muted-foreground">{g.purpose}</p>}
                <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  {[
                    [t('sponsorFunded.deposited', 'Granted'), g.deposited],
                    [t('sponsorFunded.committed', 'Committed'), g.committed],
                    [t('sponsorFunded.disbursed', 'Paid out'), g.disbursed],
                    [t('sponsorFunded.remaining', 'Remaining'), g.remaining],
                  ].map(([label, value]) => (
                    <span key={String(label)} className="flex gap-1.5">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="tabular-nums">{money(value as number)}</dd>
                    </span>
                  ))}
                </dl>
                {g.deposit_date && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('sponsorFunded.grantedOn', 'granted {{date}}', { date: when(g.deposit_date) })}
                    {g.organisations_reached > 0 &&
                      ` · ${t('sponsorFunded.reach', 'reaching {{count}} organisations', {
                        count: g.organisations_reached,
                      })}`}
                  </p>
                )}
              </div>

              <div className="rounded-b-md border border-t-0 p-4">
                {g.members.length === 0 && g.providers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('sponsorFunded.nothingFromGrant', 'Nothing has been paid from this grant yet.')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-5">
                    {g.members.length > 0 && (
                      <div>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('sponsorFunded.toMembers', 'To members')}
                        </h3>
                        <PaymentTable rows={g.members} kind="member" />
                      </div>
                    )}
                    {g.providers.length > 0 && (
                      <div>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('sponsorFunded.toProviders', 'To providers')}
                        </h3>
                        <PaymentTable rows={g.providers} kind="provider" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {data && data.grants.length > 0 && (
        <p className="mt-6 text-sm text-muted-foreground">{data.message}</p>
      )}
    </PageContainer>
  );
}
